import { Link } from "@tanstack/react-router"
import { CircleAlert } from "lucide-react"

import type { GuildEventRecord } from "@/lib/guild-events-api"

import { EventStatusBadge } from "@/components/shared/event-status-badge"
import { GuildEventRelativeTime } from "@/components/shared/guild-event-relative-time"
import { Badge } from "@/components/ui/badge"
import { getSyncStatusLabel } from "@/core/guild-events/labels"
import { formatGuildEventDateTimeWithTimezone } from "@/lib/guild-date-format"

export function getEventSourceLabel(event: GuildEventRecord): string {
  return event.scheduleId ? "Scheduled" : "Manual"
}

function hasAnnouncementIssue(event: GuildEventRecord): boolean {
  return (
    event.status === "announcement_missing" ||
    event.syncStatus === "degraded" ||
    !event.discordMessageId
  )
}

export function EventListRow({ event }: { event: GuildEventRecord }) {
  const announcementIssue = hasAnnouncementIssue(event)

  return (
    <Link
      to="/guild-events/$eventId"
      params={{ eventId: event.id }}
      className="grid gap-3 rounded-xl border border-border/70 bg-card/50 p-3 transition-colors hover:bg-card/80 md:grid-cols-[minmax(12rem,1fr)_minmax(18rem,2fr)_minmax(12rem,1fr)] md:items-center"
    >
      <section className="grid gap-1" aria-label="When">
        <span className="text-xs font-semibold tracking-wide text-muted-foreground uppercase">
          When
        </span>
        <span className="text-sm font-medium">
          {formatGuildEventDateTimeWithTimezone(event.startAt)}
        </span>
        <GuildEventRelativeTime startAt={event.startAt} />
      </section>

      <section className="grid gap-1.5" aria-label="What">
        <span className="text-xs font-semibold tracking-wide text-muted-foreground uppercase">
          What
        </span>
        <span className="text-sm font-semibold break-words">{event.title}</span>
        <div className="flex flex-wrap gap-1.5 text-xs">
          <Badge variant="outline">
            {event.eventKind === "raid" ? "Raid" : "Attendance"}
          </Badge>
          <Badge variant="secondary">{getEventSourceLabel(event)}</Badge>
          <Badge variant="secondary">
            {event.rsvpEnabled ? "RSVPs enabled" : "RSVPs disabled"}
          </Badge>
        </div>
      </section>

      <section className="grid gap-1.5" aria-label="State">
        <span className="text-xs font-semibold tracking-wide text-muted-foreground uppercase">
          State
        </span>
        <div className="flex flex-wrap gap-1.5">
          <EventStatusBadge status={event.status} />
          <Badge variant={announcementIssue ? "destructive" : "secondary"}>
            {getSyncStatusLabel(event.syncStatus)}
          </Badge>
        </div>
        {announcementIssue ? (
          <span
            className="flex items-start gap-1.5 text-xs font-medium text-destructive"
            role="alert"
          >
            <CircleAlert className="mt-0.5 size-3.5 shrink-0" />
            Announcement needs recovery
          </span>
        ) : null}
      </section>
    </Link>
  )
}
